import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { useGoogleMapsLoader } from '../../hooks/useGoogleMapsLoader';
import { BarbershopMapFilters } from './BarbershopMapFilters';

const BarbershopMapSheetComponent = ({
  open,
  onOpenChange,
  apiKey,
  barbershops,
  getBarbershopAvatarUrl,
  onReserve,
  onViewProfile,
  focusBarbershop
}) => {
  const { isLoaded } = useGoogleMapsLoader(apiKey, ['places']);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const clusterMarkersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const mapListenersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const userWatchRef = useRef(null);
  const userInteractedRef = useRef(false);
  const [mapError, setMapError] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [minRating, setMinRating] = useState(3);
  const [maxPrice, setMaxPrice] = useState(15000);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedComuna, setSelectedComuna] = useState('all');
  const getCachedUserLocation = () => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem('user_location_cache');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
        return parsed;
      }
    } catch (error) {
      return null;
    }
    return null;
  };
  const [userLocation, setUserLocation] = useState(getCachedUserLocation);
  const [locationError, setLocationError] = useState('');
  const locationOffsetKm = 10;
  const defaultCenter = { lat: -33.4123, lng: -70.5896 }; // Escuela Militar, Santiago

  const formatClpShort = (value) => `CLP ${Number(value || 0).toLocaleString('es-CL')}`;

  const mappableBarbershops = useMemo(
    () => (barbershops || []).filter((b) => typeof b.lat === 'number' && typeof b.lng === 'number'),
    [barbershops]
  );
  const getCity = (barbershop) => barbershop.city || '';
  const getComuna = (barbershop) => barbershop.comuna || '';

  const cityOptions = useMemo(() => {
    const cities = new Set();
    mappableBarbershops.forEach((b) => cities.add(getCity(b)));
    return ['all', ...Array.from(cities).filter(Boolean).sort()];
  }, [mappableBarbershops]);

  const comunaOptions = useMemo(() => {
    const comunas = new Set();
    mappableBarbershops.forEach((b) => comunas.add(getComuna(b)));
    return ['all', ...Array.from(comunas).filter(Boolean).sort()];
  }, [mappableBarbershops]);

  const filteredBarbershops = useMemo(() => {
    let result = mappableBarbershops;
    if (selectedCity !== 'all') {
      result = result.filter((b) => getCity(b) === selectedCity);
    }
    if (selectedComuna !== 'all') {
      result = result.filter((b) => getComuna(b) === selectedComuna);
    }
    result = result.filter((b) => (b.rating || 0) >= minRating);
    result = result.filter((b) => {
      const prices = (b.services || []).map((s) => Number(s.price || 0)).filter((p) => p > 0);
      const minServicePrice = prices.length ? Math.min(...prices) : 0;
      return minServicePrice === 0 || minServicePrice <= maxPrice;
    });
    if (focusBarbershop && !result.some((b) => b.id === focusBarbershop.id)) {
      result = [focusBarbershop, ...result];
    }
    return result;
  }, [mappableBarbershops, selectedCity, selectedComuna, minRating, maxPrice, focusBarbershop]);

  const clearClusterMarkers = () => {
    clusterMarkersRef.current.forEach((marker) => marker.setMap(null));
    clusterMarkersRef.current = [];
  };

  const buildInfoCard = (barbershop) => {
    const avatarUrl = getBarbershopAvatarUrl ? getBarbershopAvatarUrl(barbershop) : barbershop.profile_image;
    const rating = Number(barbershop.rating || 0).toFixed(1);
    const reviewsCount = barbershop.reviews_count || 0;
    const prices = (barbershop.services || []).map((s) => Number(s.price || 0)).filter((p) => p > 0);
    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;
    return `
      <div style="display:flex;gap:12px;align-items:center;width:100%;margin-bottom:8px;">
        <img src="${avatarUrl || ''}" alt="${barbershop.name}" loading="lazy" decoding="async" style="width:58px;height:58px;border-radius:12px;object-fit:cover;background:#333;flex-shrink:0;" />
        <div style="color:#f5f5f5;min-width:0;flex:1;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <div style="font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${barbershop.name}</div>
            <span style="background:#d4af37;color:#000;border-radius:10px;padding:2px 6px;font-weight:700;font-size:12px;">⭐ ${rating}</span>
          </div>
          <div style="font-size:12px;color:#c7c7c7;margin:6px 0 6px 0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${barbershop.address || ''}</div>
          <div style="display:flex;align-items:center;gap:10px;font-size:12px;color:#bcbcbc;">
            <span>${reviewsCount} reseñas</span>
            ${minPrice > 0 ? `<span style="color:#f5d979;font-weight:600;">${formatClpShort(minPrice)}${maxPrice && maxPrice !== minPrice ? ` - ${formatClpShort(maxPrice)}` : ''}</span>` : ''}
          </div>
        </div>
      </div>
    `;
  };

  const buildInfoActions = (barbershop, contentId) => `
    <div id="${contentId}" style="display:none;gap:8px;flex-wrap:wrap;margin-top:6px;">
      <button data-action="reserve" style="padding:8px 12px;border-radius:8px;border:none;background:#d4af37;color:#000;font-weight:700;cursor:pointer;">Reservar</button>
      <button data-action="directions" style="padding:8px 12px;border-radius:8px;border:1px solid #3a3a3a;background:#111;color:#fff;font-weight:600;cursor:pointer;">Cómo llegar</button>
      <button data-action="profile" style="padding:8px 12px;border-radius:8px;border:1px solid #3a3a3a;background:#111;color:#fff;font-weight:600;cursor:pointer;">Ver perfil</button>
    </div>
  `;

  const openInfoWindowForList = (list, marker) => {
    const contentId = `shop-actions-${list.map((b) => b.id).join('-')}`;
    const content = `
      <div class="map-info-card" style="min-width:320px;max-width:380px;font-family:Arial,sans-serif;background:#0f0f0f;border:1px solid #2b2b2b;border-radius:16px;padding:14px;">
        ${list
          .map((b, index) => `<div data-card="${b.id}" style="cursor:pointer;${index > 0 ? 'margin-top:10px;padding-top:10px;' : ''}">${buildInfoCard(b)}</div>`)
          .join('')}
        ${list.map((b) => buildInfoActions(b, `${contentId}-${b.id}`)).join('')}
      </div>
    `;
    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapInstanceRef.current, marker);
    window.google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
      list.forEach((b) => {
        const container = document.getElementById(`${contentId}-${b.id}`);
        if (!container) return;
        const card = container.parentElement?.querySelector(`[data-card="${b.id}"]`);
        card?.addEventListener('click', () => {
          const isHidden = container.style.display === 'none' || !container.style.display;
          container.style.display = isHidden ? 'flex' : 'none';
        });
        container.querySelector('[data-action="reserve"]')?.addEventListener('click', () => onReserve?.(b));
        container.querySelector('[data-action="profile"]')?.addEventListener('click', () => onViewProfile?.(b));
        container.querySelector('[data-action="directions"]')?.addEventListener('click', () => {
          const query = b.address || `${b.lat},${b.lng}`;
          window.open(`https://maps.google.com/maps?q=${encodeURIComponent(query)}`, '_blank');
        });
      });
    });
  };

  const applyClustering = () => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const zoom = map.getZoom() || 12;
    const gridSize = Math.max(0.02, 0.6 / Math.pow(2, zoom - 10));

    const clusters = new Map();
    markersRef.current.forEach(({ marker, barbershop }) => {
      const pos = marker.getPosition();
      if (!pos) return;
      const latKey = Math.floor(pos.lat() / gridSize);
      const lngKey = Math.floor(pos.lng() / gridSize);
      const key = `${latKey}:${lngKey}`;
      if (!clusters.has(key)) clusters.set(key, []);
      clusters.get(key).push({ marker, barbershop });
    });

    clearClusterMarkers();

    clusters.forEach((group) => {
      if (group.length <= 1) {
        group[0].marker.setVisible(true);
        return;
      }

      group.forEach(({ marker }) => marker.setVisible(false));
      const avgLat = group.reduce((sum, m) => sum + m.marker.getPosition().lat(), 0) / group.length;
      const avgLng = group.reduce((sum, m) => sum + m.marker.getPosition().lng(), 0) / group.length;

      const clusterMarker = new window.google.maps.Marker({
        position: { lat: avgLat, lng: avgLng },
        map,
        label: {
          text: String(group.length),
          color: '#fff',
          fontWeight: '700'
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#f59e0b',
          fillOpacity: 0.9,
          strokeColor: '#111',
          strokeWeight: 2,
          scale: Math.min(20, 12 + group.length)
        }
      });

      clusterMarker.addListener('click', () => {
        const list = group.map((g) => g.barbershop);
        openInfoWindowForList(list, clusterMarker);
      });

      clusterMarkersRef.current.push(clusterMarker);
    });
  };

  useEffect(() => {
    if (!open || !isLoaded || !mapRef.current) return;
    let cancelled = false;
    setMapError('');
    userInteractedRef.current = false;
    window.gm_authFailure = () => {
      setMapError('No se pudo activar Google Maps. Revisa la clave y las restricciones del dominio.');
    };

    const initMap = () => {
      if (cancelled || !open || !isLoaded || !mapRef.current) return;
      const rect = mapRef.current.getBoundingClientRect();
      if (rect.width < 120 || rect.height < 120) {
        setTimeout(initMap, 150);
        return;
      }

      if (mapInstanceRef.current && mapInstanceRef.current.getDiv?.() !== mapRef.current) {
        if (userMarkerRef.current) {
          userMarkerRef.current.setMap(null);
          userMarkerRef.current = null;
        }
        mapInstanceRef.current = null;
        infoWindowRef.current = null;
        markersRef.current = [];
        clusterMarkersRef.current = [];
        mapListenersRef.current = [];
      }

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: defaultCenter,
          zoom: 13,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          clickableIcons: false
        });
      }

      if (!infoWindowRef.current) {
        infoWindowRef.current = new window.google.maps.InfoWindow();
      }

      markersRef.current.forEach((entry) => entry.marker.setMap(null));
      markersRef.current = [];
      clearClusterMarkers();
      mapListenersRef.current.forEach((listener) => listener.remove());
      mapListenersRef.current = [];

      filteredBarbershops.forEach((barbershop) => {
        const marker = new window.google.maps.Marker({
          position: { lat: barbershop.lat, lng: barbershop.lng },
          map: mapInstanceRef.current,
          title: barbershop.name
        });

        marker.addListener('click', () => {
          const sameLocation = filteredBarbershops.filter((item) => {
            const latMatch = Math.abs(item.lat - barbershop.lat) < 0.00005;
            const lngMatch = Math.abs(item.lng - barbershop.lng) < 0.00005;
            return latMatch && lngMatch;
          });
          openInfoWindowForList(sameLocation.length > 0 ? sameLocation : [barbershop], marker);
        });

        markersRef.current.push({ marker, barbershop });
      });

      if (filteredBarbershops.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        filteredBarbershops.forEach((b) => bounds.extend({ lat: b.lat, lng: b.lng }));
        mapInstanceRef.current.fitBounds(bounds);
      } else {
        mapInstanceRef.current.setCenter(defaultCenter);
        mapInstanceRef.current.setZoom(13);
      }

      mapListenersRef.current.push(
        mapInstanceRef.current.addListener('zoom_changed', () => {
          userInteractedRef.current = true;
          applyClustering();
        }),
        mapInstanceRef.current.addListener('dragstart', () => {
          userInteractedRef.current = true;
        }),
        mapInstanceRef.current.addListener('dragend', applyClustering),
        mapInstanceRef.current.addListener('idle', applyClustering)
      );

      applyClustering();
      window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
      setMapReady(true);
    };

    initMap();
    return () => {
      cancelled = true;
    };
  }, [open, isLoaded, filteredBarbershops, getBarbershopAvatarUrl, onReserve, onViewProfile]);

  useEffect(() => {
    if (!open) return;
    const handler = (event) => {
      if (event?.detail === 'PROJECT_DENIED') {
        setMapError('Google Maps rechazó la API key. Revisa restricciones del dominio y billing.');
      }
    };
    window.addEventListener('gmaps-error', handler);
    if (window.__gmapsError === 'PROJECT_DENIED') {
      setMapError('Google Maps rechazó la API key. Revisa restricciones del dominio y billing.');
    }
    return () => window.removeEventListener('gmaps-error', handler);
  }, [open]);

  const getOffsetCenter = (coords) => {
    const offsetLat = (locationOffsetKm / 111).toFixed(6);
    return { lat: coords.lat - Number(offsetLat), lng: coords.lng };
  };

  const ensureUserLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(coords);
        setLocationError('');
        try {
          window.localStorage.setItem('user_location_cache', JSON.stringify(coords));
        } catch (error) {
          // ignore storage errors
        }
      },
      () => {
        setLocationError('No se pudo acceder a tu ubicación.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  useEffect(() => {
    if (!open || !isLoaded || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const recenter = () => {
      if (!window.google?.maps) return;
      if (userInteractedRef.current) return;
      window.google.maps.event.trigger(map, 'resize');
      if (userLocation) {
        map.setCenter(getOffsetCenter(userLocation));
        map.setZoom(12);
        return;
      }
      if (filteredBarbershops.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        filteredBarbershops.forEach((b) => bounds.extend({ lat: b.lat, lng: b.lng }));
        map.fitBounds(bounds);
      } else {
        map.setCenter(defaultCenter);
        map.setZoom(13);
      }
    };
    const raf = requestAnimationFrame(recenter);
    const handle = setTimeout(recenter, 600);
    const handleLate = setTimeout(recenter, 1100);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(handle);
      clearTimeout(handleLate);
    };
  }, [open, isLoaded, filteredBarbershops, defaultCenter, userLocation]);

  useEffect(() => {
    if (!open) return;
    userInteractedRef.current = false;
    if (!navigator.geolocation) return;
    if (userWatchRef.current !== null) return;
    userWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(coords);
        setLocationError('');
        try {
          window.localStorage.setItem('user_location_cache', JSON.stringify(coords));
        } catch (error) {
          // ignore storage errors
        }
      },
      () => {
        setLocationError('No se pudo acceder a tu ubicación.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
    ensureUserLocation();
    return () => {
      if (userWatchRef.current !== null) {
        navigator.geolocation.clearWatch(userWatchRef.current);
        userWatchRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !isLoaded || !mapInstanceRef.current) return;
    if (!userLocation || !window.google?.maps) return;
    if (!userMarkerRef.current) {
      userMarkerRef.current = new window.google.maps.Marker({
        position: userLocation,
        map: mapInstanceRef.current,
        title: 'Tu ubicación',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#3b82f6',
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 7
        },
        zIndex: 9999
      });
    } else {
      userMarkerRef.current.setPosition(userLocation);
    }
  }, [open, isLoaded, userLocation]);

  useEffect(() => {
    if (!focusBarbershop || !infoWindowRef.current) return;
    const entry = markersRef.current.find((item) => item.barbershop.id === focusBarbershop.id);
    if (entry) {
      mapInstanceRef.current.setCenter({ lat: focusBarbershop.lat, lng: focusBarbershop.lng });
      mapInstanceRef.current.setZoom(14);
      openInfoWindowForList([focusBarbershop], entry.marker);
    }
  }, [focusBarbershop]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={`bg-gray-950 border-gray-800 text-white flex flex-col ${focusBarbershop ? 'h-[65vh]' : 'h-[80vh]'} sm:h-[78vh] sm:bottom-6 sm:rounded-2xl rounded-t-2xl`}
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="text-white flex items-center gap-2">
            <MapPin className="w-4 h-4 text-amber-400" />
            Mapa de barberías
          </SheetTitle>
        </SheetHeader>

        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="text-xs text-gray-400">
            {selectedCity === 'all' ? 'Mostrando todas las ciudades' : `Ciudad: ${selectedCity}`}
            {locationError && !userLocation && (
              <span className="block text-[11px] text-amber-300 mt-1">{locationError}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-gray-700 text-gray-200"
              onClick={() => {
                if (!userLocation) ensureUserLocation();
                if (mapInstanceRef.current && userLocation) {
                  userInteractedRef.current = false;
                  mapInstanceRef.current.setCenter(getOffsetCenter(userLocation));
                  mapInstanceRef.current.setZoom(12);
                }
              }}
            >
              Centrar
            </Button>
            <BarbershopMapFilters
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              cityOptions={cityOptions}
              comunaOptions={comunaOptions}
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              selectedComuna={selectedComuna}
              setSelectedComuna={setSelectedComuna}
              minRating={minRating}
              setMinRating={setMinRating}
              maxPrice={maxPrice}
              setMaxPrice={setMaxPrice}
            />
          </div>
        </div>

        <div className="flex-1 min-h-[320px] w-full rounded-xl overflow-hidden border border-gray-800">
          {!apiKey && (
            <div className="h-full w-full flex items-center justify-center text-sm text-gray-400">
              Falta configurar la API de Google Maps (REACT_APP_GOOGLE_MAPS_API_KEY).
            </div>
          )}
          {apiKey && !isLoaded && (
            <div className="h-full w-full flex items-center justify-center text-sm text-gray-400">
              Cargando mapa...
            </div>
          )}
          {apiKey && isLoaded && !mapError && (
            <div ref={mapRef} className="h-full w-full" />
          )}
          {mapError && (
            <div className="h-full w-full flex items-center justify-center text-sm text-amber-200">
              {mapError}
            </div>
          )}
        </div>

        {filteredBarbershops.length === 0 && mapReady && (
          <div className="mt-3 text-xs text-gray-400">
            No hay barberías con ubicación para este filtro.
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="outline" className="border-gray-700 text-gray-200" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const BarbershopMapSheet = React.memo(BarbershopMapSheetComponent);
